import { ContractsApi, ContractMainRequest, ContractExtraRequest, ContractResponse } from '@oncampusabroad/api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { initialApiState, ApiCallBase, createApiConfiguration } from './apiConfiguration';
import { formatDatesInObject } from '../../utils/date-formatter';

interface ContractMainCall extends ApiCallBase {
  response: ContractResponse;
}

interface ContractExtraCall extends ApiCallBase {
  response: ContractResponse;
}

interface State {
  mainContract: ContractMainCall;
  extraContract: ContractExtraCall;
}

const initialState: State = {
  mainContract: initialApiState,
  extraContract: initialApiState,
};

export const createMainContract = createAsyncThunk(
  'contracts/createMainContract',
  async (request: ContractMainRequest, { rejectWithValue }) => {
    try {
      const contractsApi = new ContractsApi(createApiConfiguration());

      const formattedRequest = formatDatesInObject(request);
      
      const response = await contractsApi.mainContract(formattedRequest);
      return response.data;
    } catch (error: any) {
      console.error('Error creating main contract:', error);
      return rejectWithValue({
        i18nMessage: error.response?.data?.i18nMessage ?? 'api.error.server_error',
        status: error.response?.status,
      });
    }
  }
);

export const createExtraContract = createAsyncThunk(
  'contracts/createExtraContract',
  async ({ request, token }: { request: ContractExtraRequest; token?: string }, { rejectWithValue }) => {
    try {
      if (!token) {
        return rejectWithValue({ i18nMessage: 'api.error.login_required' });
      }
      const config = createApiConfiguration(token);
      const contractsApi = new ContractsApi(config);
      
      const response = await contractsApi.extraContract(request);
      return response.data;
    } catch (error: any) {
      console.error('Error creating extra contract:', error);
      return rejectWithValue({
        i18nMessage: error.response?.data?.i18nMessage ?? 'api.error.server_error',
        status: error.response?.status,
      });
    }
  }
);

const ContractsSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    resetMainContract: (state) => {
      state.mainContract = {
        ...initialApiState,
        response: state.mainContract.response,
      };
    },
    resetMainContractResponse: (state) => {
      state.mainContract = {
        ...initialApiState,
      };
    },
    resetExtraContract: (state) => {
      state.extraContract = {
        ...initialApiState,
        response: state.extraContract.response,
      };
    },
    resetExtraContractResponse: (state) => {
      state.extraContract = {
        ...initialApiState,
      };
    },
  },
  extraReducers: (builder) => {
    // Main Contract
    builder.addCase(createMainContract.pending, (state) => {
      state.mainContract.loading = 'pending';
    });
    builder.addCase(createMainContract.fulfilled, (state, action) => {
      state.mainContract.loading = 'succeeded';
      state.mainContract.response = action.payload;
    });
    builder.addCase(createMainContract.rejected, (state, action) => {
      state.mainContract.loading = 'failed';
      state.mainContract.error = action.payload;
    });
    
    // Extra Contract
    builder.addCase(createExtraContract.pending, (state) => {
      state.extraContract.loading = 'pending';
    });
    builder.addCase(createExtraContract.fulfilled, (state, action) => {
      state.extraContract.loading = 'succeeded';
      state.extraContract.response = action.payload;
    });
    builder.addCase(createExtraContract.rejected, (state, action) => {
      state.extraContract.loading = 'failed';
      state.extraContract.error = action.payload;
    });
  },
});

export const {
  resetMainContract,
  resetMainContractResponse,
  resetExtraContract,
  resetExtraContractResponse,
} = ContractsSlice.actions;

export default ContractsSlice.reducer; 